'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { ADSENSE_CLIENT, adsQueue } from './AdSenseLoader';
import { TOOL_CONTENT_WIDTH, type ToolContentWidth } from './toolContentWidth';

/**
 * One manual AdSense display unit at the end of each tool page (rendered by RelatedTools, after
 * the guide), which gets a FRESH ad each time the visitor opens a different tool.
 *
 * Why: tool links are client-side (<Link>), and auto ads only place ads on a FULL page load, so
 * before this only the first page of a visit could earn (~3 pages per visit). A manual unit can
 * be requested again when the content changes.
 *
 * 🚨 AdSense policy: no refresh "without the user requesting a refresh". A new ad is requested
 * ONLY when the pathname changes, i.e. the visitor clicked to a different tool. Never add a timer,
 * a visibility/scroll trigger, or a re-request on state changes inside the same tool.
 *
 * How: the unit is keyed on the pathname, so React unmounts the old <ins> and mounts a fresh one
 * (AdSense fills each <ins> once; pushing again for a filled one throws "All ins elements ...
 * already have ads in them"). The push is guarded so StrictMode's double effect can't request twice.
 *
 * Placement rules (accidental clicks are invalid traffic): far from Copy / Download / Apply buttons
 * and file drop zones, labelled "Advertisement", height reserved so nothing below jumps. An unfilled
 * unit is hidden (allowed: support.google.com/adsense/answer/10762946), see `.tool-ad` in globals.css.
 *
 * Renders nothing until NEXT_PUBLIC_ADSENSE_TOOL_SLOT (the ad unit's data-ad-slot id from AdSense →
 * Ads → By ad unit → Display ads) is set, so this can ship before the unit exists.
 */
const SLOT = process.env.NEXT_PUBLIC_ADSENSE_TOOL_SLOT;

export default function ToolSwitchAd({ width = '7xl' }: { width?: ToolContentWidth }) {
  const pathname = usePathname();
  if (!SLOT) return null;
  return <ToolAdUnit key={pathname} slot={SLOT} width={width} />;
}

function ToolAdUnit({ slot, width }: { slot: string; width: ToolContentWidth }) {
  const insRef = useRef<HTMLModElement>(null);
  const requested = useRef(false);

  useEffect(() => {
    const ins = insRef.current;
    if (!ins || requested.current || ins.hasAttribute('data-adsbygoogle-status')) return;
    requested.current = true;
    try {
      adsQueue().push({});
    } catch {
      // adsbygoogle throws on a double fill or when blocked; an empty slot is hidden by CSS.
    }
  }, []);

  return (
    <aside aria-label="Advertisement" className="tool-ad px-4 md:px-6 mt-2 mb-8">
      <div className={`${TOOL_CONTENT_WIDTH[width]} mx-auto`}>
        <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Advertisement</p>
        <div className="min-h-[250px]">
          <ins
            ref={insRef}
            className="adsbygoogle block"
            data-ad-client={ADSENSE_CLIENT}
            data-ad-slot={slot}
            data-ad-format="auto"
            data-full-width-responsive="true"
          />
        </div>
      </div>
    </aside>
  );
}
